<template>
    <ion-page>
        <ion-content 
            class="main-background playlist-view-page"
            :fullscreen="true"
            slot="fixed"
            :scroll-y="false">

            <ion-content class="playlist-container bg-transparent" :scroll-y="false">
                <ion-header mode="md" class="playlist-view-header">
                    <ion-toolbar class="playlist-view-toolbar">
                        <ion-title class="playlist-toolbar-title">Микс</ion-title>
                        <ion-buttons slot="end">
                            <ion-button
                                class="playlist-header-button playlist-close-btn"
                                fill="clear"
                                @click="closePlaylist()"
                                v-show="!downloadProgressBar.downloading"
                            >Закрыть</ion-button>
                        </ion-buttons>
                    </ion-toolbar>
                </ion-header>
            <ion-list class="playlist-content" lines="none">
                <section class="playlist-hero" v-if="selectedPlaylist.name">
                    <div class="playlist-hero__glow" aria-hidden="true"></div>
                    <div class="playlist-hero__inner">
                        <div class="playlist-hero__art">
                            <img
                                :src="programLogo || getProgramImage(selectedPlaylist.programShortName)"
                                alt=""
                            />
                        </div>
                        <div class="playlist-hero__text">
                            <h1 class="playlist-hero__name">{{ selectedPlaylist.name }}</h1>
                            <p v-if="selectedPlaylist.programName" class="playlist-hero__program">{{ selectedPlaylist.programName }}</p>
                            <div class="playlist-hero__chips">
                                <span class="playlist-chip">
                                    <ion-icon :icon="musicalNotesOutline"></ion-icon>
                                    {{ playlistTrackCount }} {{ trackCountLabel }}
                                </span>
                                <span class="playlist-chip">
                                    <ion-icon :icon="timeOutline"></ion-icon>
                                    {{ mediaUtils.formatTime(
                                        mediaUtils.getTotalPlaylistDuration(selectedPlaylist.tracks)
                                    ) }}
                                </span>
                                <span v-if="selectedPlaylist.offline" class="playlist-chip playlist-chip--success">
                                    <ion-icon :icon="checkmarkCircle"></ion-icon>
                                    Оффлайн
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="playlist-hero__actions" v-show="!selectedPlaylist.isRelease && !downloadProgressBar.downloading">
                        <ion-button
                            fill="outline"
                            size="small"
                            class="hero-action-btn"
                            @click="goEditPlaylist()"
                        >
                            <ion-icon slot="start" :icon="createOutline"></ion-icon>
                            Изменить
                        </ion-button>
                        <ion-button
                            fill="outline"
                            size="small"
                            class="hero-action-btn hero-action-btn--danger"
                            @click="confirmDeletePlaylist()"
                        >
                            <ion-icon slot="start" :icon="trashOutline"></ion-icon>
                            Удалить
                        </ion-button>
                    </div>
                </section>
                <div class="download-button">
                    <ion-button
                    fill="outline"
                    class="playlist-header-button"
                    @click="downloadPlaylist(selectedPlaylist)"
                    v-show="
                        !selectedPlaylist.offline &&
                        !downloadProgressBar.downloading &&
                        globalContext.isMobile
                    "
                    >Загрузить оффлайн</ion-button
                    >
                    <div
                    class="column-buttons"
                    v-show="
                        selectedPlaylist.offline && !downloadProgressBar.downloading
                    "
                    >
                    <ion-button
                        fill="outline"
                        class="playlist-header-button"
                        :disabled="true"
                        >Доступно оффлайн</ion-button
                    >
                    <ion-button
                        fill="outline"
                        class="playlist-header-button remove-download-button"
                        @click="removeDownloadedPlaylist(selectedPlaylist)"
                    >
                        <ion-icon slot="icon-only" :icon="trashOutline"></ion-icon>
                    </ion-button>
                    </div>
                </div>
                <div
                    class="column-buttons"
                    v-show="downloadProgressBar.downloading"
                >
                    <ion-button
                    fill="outline"
                    class="playlist-header-button"
                    @click="stopDownload()"
                    >Отмена</ion-button
                    >
                    <ion-text class="playlist-header-button ion-text-center"
                    >{{ downloadProgressBar.downloaded }} Mb</ion-text
                    >
                    <ion-text class="playlist-header-button ion-text-center"
                    >Не закрывайте окно до завершения загрузки</ion-text
                    >
                </div>
                <ion-progress-bar
                    :value="
                    downloadProgressBar.currentValue /
                    downloadProgressBar.maxValue
                    "
                    class="download-progress-bar"
                    v-show="downloadProgressBar.downloading"
                ></ion-progress-bar>
                <ion-content :forceOverscroll="true" class="playlist-tracks-scroll">
                    <ion-item
                    v-for="(track, idx) in selectedPlaylist.tracks"
                    :key="idx"
                    @click="selectTrack(idx)"
                    class="track-item"
                    :class="{ 'playing': isTrackRowActive(idx) }"
                    >
                    <div class="track-number">
                        <span v-if="!isTrackRowActive(idx)">{{ idx + 1 }}</span>
                        <ion-icon v-else :icon="play" class="playing-icon"></ion-icon>
                    </div>

                    <div class="track-info">
                        <div class="track-name">{{ mediaUtils.getTrackName(track) }}</div>
                        <div class="track-duration">{{ mediaUtils.formatTime(track.duration) }}</div>
                    </div>

                    <div class="track-actions">
                        <div class="play-button-wrapper">
                            <ion-icon 
                                :icon="isTrackRowActive(idx) ? pause : play"
                                class="play-icon"
                            ></ion-icon>
                        </div>
                    </div>
                    </ion-item>
                </ion-content>
            </ion-list>
          </ion-content>
            
        </ion-content>
    </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from "@/model/globalcontext.model";
import { MediaProgressBar } from "@/model/mediaprogressbar.model";
import { PlayerModes } from "@/model/player.mode";
import { PlayerBar } from "@/model/playerbar.model";
import { Playlist } from "@/model/playlist.model";
import FilesystemCustomUtils from "@/services/filesystem.custom.utils";
import LogoService from "@/services/logo.service";
import MediaUtils from "@/services/media.utils";
import UnicodeUtils from "@/services/unicode.utils";
import { CapacitorHttp } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonPage,
  IonProgressBar,
  IonText,
  IonTitle,
  IonToolbar,
  alertController,
  isPlatform,
} from "@ionic/vue";
import axios from "axios";
import { RmxAudioPlayer } from "capacitor-plugin-playlist";
import dayjs from "dayjs";
import {
  checkmarkCircle,
  createOutline,
  musicalNotesOutline,
  pause,
  play,
  timeOutline,
  trashOutline
} from "ionicons/icons";
import { defineComponent, inject } from "vue";
// @ts-ignore
import { MittEmitter } from "mitt-emitter";

export default defineComponent({
    name: "PlaylistListView",
    components: {
        IonContent,
        IonPage,
        IonList,
        IonItem,
        IonButton,
        IonIcon,
        IonText,
        IonHeader,
        IonTitle,
        IonButtons,
        IonToolbar,
        IonProgressBar,
    },

    setup() {
        const globalContext = inject("globalContext") as GlobalContext;
        const PLAYLISTS_DIRECTORY = "playlists";
        const PROGRAMS_PATH = "programs.json";
        const PLAYLIST_PATH = "playlist.json";
        const mediaUtils = new MediaUtils();
        const emitter = inject("emitter") as MittEmitter;
        const unicodeUtils = new UnicodeUtils();
        const filesystemCustomUtils = new FilesystemCustomUtils();
        const playerBar = inject("playerBar") as PlayerBar;
        const audioPlayer = inject("audioPlayer") as RmxAudioPlayer;

        return {
            globalContext,
            PLAYLISTS_DIRECTORY,
            PROGRAMS_PATH,
            PLAYLIST_PATH,
            mediaUtils,
            emitter,
            unicodeUtils,
            filesystemCustomUtils,
            playerBar,
            audioPlayer,
            trashOutline,
            createOutline,
            musicalNotesOutline,
            checkmarkCircle,
            play,
            pause,
            timeOutline
        }
    },

    data() {
        return {
            selectedPlaylist: new Playlist("", "", ""),
            downloaded: 0,
            downloading: false,
            downloadProgressBar: new MediaProgressBar(),
            isFirstSelect: true,
            programLogo: "",
            logoService: new LogoService(),
        };
    },

    computed: {
        playlistTrackCount(): number {
            return this.selectedPlaylist.tracks?.length ?? 0;
        },
        trackCountLabel(): string {
            const n = this.playlistTrackCount;
            const mod10 = n % 10;
            const mod100 = n % 100;
            if (mod100 >= 11 && mod100 <= 14) return "треков";
            if (mod10 === 1) return "трек";
            if (mod10 >= 2 && mod10 <= 4) return "трека";
            return "треков";
        },
    },

    mounted() {
        this.isFirstSelect = true;
    },

    ionViewWillEnter() {
        Preferences.get({ key: "playlistMeta" }).then((playlistMeta) => {
            if (playlistMeta.value) {
                this.selectedPlaylist = JSON.parse(playlistMeta.value);
                this.loadProgramLogo();
            }
        });
    },

    methods: {
        closePlaylist() {
            this.$router.go(-1);
        },

        goEditPlaylist() {
            if (!this.selectedPlaylist.id || this.selectedPlaylist.isRelease) return;
            this.$router.push({
                path: "/playlist-list",
                query: { edit: this.selectedPlaylist.id },
            });
        },

        async confirmDeletePlaylist() {
            if (!this.selectedPlaylist.id || this.selectedPlaylist.isRelease) return;
            const alert = await alertController.create({
                header: "Удалить микс?",
                message: "Плейлист будет удалён без возможности восстановления.",
                buttons: [
                    { text: "Отмена", role: "cancel", cssClass: "secondary" },
                    {
                        text: "Удалить",
                        role: "destructive",
                        handler: () => {
                            this.deletePlaylist();
                        },
                    },
                ],
            });
            await alert.present();
        },

        async deletePlaylist() {
            const playlistId = this.selectedPlaylist.id;
            if (!playlistId) return;

            const isNative = isPlatform("ios") || isPlatform("android");
            const filesystemCustomUtils = new FilesystemCustomUtils();
            if (isNative) {
                filesystemCustomUtils.removePlaylistDirectory(playlistId);
            } else if (!this.globalContext.offlineMode) {
                await axios
                    .delete("/api/playlist", { data: { name: playlistId } })
                    .catch((e) => console.warn("playlist delete", e));
            }

            await Preferences.remove({ key: "playlistMeta" });
            this.$router.replace("/playlist-list");
        },

        async downloadPlaylist(playlist: Playlist, withProgress?: boolean) {
            // Check if running on web platform
            if (this.globalContext.isMobile === false) {
                console.warn('Playlist download is not supported on web platform');
                this.showAlert('Недоступно', 'Загрузка плейлистов недоступна в веб-версии приложения');
                return;
            }

            const filesystemCustomUtils = new FilesystemCustomUtils();
            if (typeof withProgress === "undefined" || withProgress) {
                filesystemCustomUtils.setProgressBar(this.downloadProgressBar);
            }
            
            try {
                await filesystemCustomUtils.downloadPlaylist(playlist);

                playlist.offline = true;
                playlist.offlineDate = `${dayjs().date()}.${String(
                    dayjs().month() + 1
                ).padStart(2, "0")}.${dayjs().year()}`;
                
                // Update the playlist metadata locally
                await filesystemCustomUtils.storeFile(
                    this.unicodeUtils.toBase64(JSON.stringify(playlist)),
                    this.PLAYLIST_PATH,
                    `${this.PLAYLISTS_DIRECTORY}/${playlist.id}`
                );

                // Sync with API
                const tokenQuery = await Preferences.get({ key: "jwt" });
                const token = tokenQuery.value;

                if (token) {
                    CapacitorHttp.put({
                        url: `${process.env.VUE_APP_API_URL}api/playlist`,
                        headers: { Authorization: `Bearer ${token}` },
                        data: {
                            name: playlist.id,
                            content: JSON.stringify(playlist),
                        },
                    }).catch(err => {
                        console.log('Failed to sync playlist download with API', err);
                    });
                }
            } catch (error) {
                console.error('Failed to download playlist', error);
                this.downloadProgressBar.downloading = false;
            }
        },

        async stopDownload() {
            // Clean up partial files
            if (this.selectedPlaylist && this.selectedPlaylist.id) {
                const filesystemCustomUtils = new FilesystemCustomUtils();
                await filesystemCustomUtils.cleanupPartialDownloads(this.selectedPlaylist);
            }
            
            // Reset playlist offline state when canceling
            this.selectedPlaylist.offline = false;
            this.selectedPlaylist.offlineDate = '';
            
            this.downloadProgressBar.currentValue = 0;
            this.downloadProgressBar.maxValue = 0;
            this.downloadProgressBar.downloaded = 0;
            this.downloadProgressBar.downloading = false;
        },

        async removeDownloadedPlaylist(playlist: Playlist) {
            const filesystemCustomUtils = new FilesystemCustomUtils();
            await filesystemCustomUtils.removeDownloadedPlaylist(playlist);
            playlist.offline = false;
            playlist.offlineDate = '';
            
            // Update the playlist metadata locally
            await filesystemCustomUtils.storeFile(
                this.unicodeUtils.toBase64(JSON.stringify(playlist)),
                this.PLAYLIST_PATH,
                `${this.PLAYLISTS_DIRECTORY}/${playlist.id}`
            );

            // Sync with API
            const tokenQuery = await Preferences.get({ key: "jwt" });
            const token = tokenQuery.value;

            if (token) {
                CapacitorHttp.put({
                    url: `${process.env.VUE_APP_API_URL}api/playlist`,
                    headers: { Authorization: `Bearer ${token}` },
                    data: {
                        name: playlist.id,
                        content: JSON.stringify(playlist),
                    },
                }).catch(err => {
                    console.log('Failed to sync playlist removal with API', err);
                });
            }
        },

        isTrackRowActive(idx: number): boolean {
            return (
                !this.isFirstSelect &&
                this.playerBar.currentTrackIdx === idx &&
                !!(this.playerBar.visible || this.playerBar.microMode)
            );
        },

        selectTrack(idx: number) {
            if (this.audioPlayer && this.audioPlayer.isPlaying) {
                this.audioPlayer.pause();
            }

            this.playerBar.visible = true;
            this.playerBar.isFM = false;
            this.playerBar.currentTrackIdx = idx;
            this.playerBar.playlistName = this.selectedPlaylist.name;
            this.playerBar.offline = this.selectedPlaylist.offline;
            this.playerBar.offlineDirectory = `${this.PLAYLISTS_DIRECTORY}/${this.selectedPlaylist.id}`;
            this.playerBar.musicList = this.selectedPlaylist.tracks;
            this.playerBar.playlistImage = `${this.selectedPlaylist.programShortName}.png`;
            this.playerBar.playlistImageDir = `${this.PLAYLISTS_DIRECTORY}/${this.selectedPlaylist.id}`;
            this.playerBar.programShortName = this.selectedPlaylist.programShortName;
            this.playerBar.currentMode = PlayerModes.PLAYLIST;

            if (this.isFirstSelect) {
                this.isFirstSelect = false;
                this.emitter.emit("music-player-open-extended", true);
                this.emitter.emit("music-player-loaded-list", true);
            } else {
                this.emitter.emit("music-player-open-extended", true);
                this.emitter.emit("music-player-restart-and-play", true);
            }
        },

        async loadProgramLogo() {
            if (this.selectedPlaylist.programShortName) {
                try {
                    this.programLogo = await this.logoService.getProgramLogo(this.selectedPlaylist.programShortName);
                } catch (error) {
                    console.error(`Failed to load logo for ${this.selectedPlaylist.programShortName}:`, error);
                }
            }
        },

        getProgramImage(shortName: string): string {
            if (!shortName || shortName.trim() === '') {
                return require('@/theme/liga-logo-white.png');
            }
            
            try {
                return require(`@/theme/programs/${shortName}.png`);
            } catch (error) {
                console.warn(`Program image not found for ${shortName}, using Liga logo as fallback`);
                return require('@/theme/liga-logo-white.png');
            }
        },

        async showAlert(alertTitle: string, alertMessage: string) {
            const alert = await alertController.create({
                header: alertTitle,
                message: alertMessage,
                buttons: ['OK'],
            });

            await alert.present();
        },
    }
})
</script>

<style scoped>
.playlist-view-header ion-toolbar {
  --background: linear-gradient(135deg, #c2410c 0%, #9a3412 55%, #7c2d12 100%);
  --border-width: 0;
  --min-height: 52px;
}

.playlist-toolbar-title {
  color: rgba(255, 255, 255, 0.95) !important;
  font-weight: 700;
  letter-spacing: 0.06em;
  font-size: 0.82rem;
  text-transform: uppercase;
}

.playlist-close-btn {
  font-weight: 600;
  --color: rgba(255, 255, 255, 0.95) !important;
  margin-left: 4px;
}

.playlist-hero {
  position: relative;
  overflow: hidden;
  margin: 12px 14px 18px;
  padding: 1.1rem 1.15rem 1rem;
  border-radius: 22px;
  background: linear-gradient(
    155deg,
    rgba(38, 38, 46, 0.97) 0%,
    rgba(24, 24, 30, 0.99) 48%,
    rgba(18, 18, 22, 1) 100%
  );
  border: 1px solid rgba(255, 140, 0, 0.22);
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.playlist-hero__glow {
  position: absolute;
  top: -45%;
  right: -25%;
  width: 70%;
  height: 95%;
  background: radial-gradient(
    circle,
    rgba(255, 140, 0, 0.16) 0%,
    transparent 68%
  );
  pointer-events: none;
}

.playlist-hero__inner {
  display: flex;
  gap: 1.1rem;
  align-items: flex-start;
  position: relative;
  z-index: 1;
}

.playlist-hero__art {
  flex-shrink: 0;
  width: 108px;
  height: 108px;
  border-radius: 18px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.35);
  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.5),
    inset 0 0 0 1px rgba(255, 255, 255, 0.07);
}

.playlist-hero__art img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.playlist-hero__text {
  flex: 1;
  min-width: 0;
}

.playlist-hero__name {
  margin: 0 0 0.35rem;
  font-size: 1.28rem;
  font-weight: 700;
  font-family: "Montserrat", sans-serif;
  color: rgba(255, 255, 255, 0.97);
  line-height: 1.28;
  letter-spacing: 0.02em;
}

.playlist-hero__program {
  margin: 0 0 0.85rem;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.52);
  font-family: "Montserrat", sans-serif;
  line-height: 1.35;
}

.playlist-hero__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.playlist-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  font-family: "Montserrat", sans-serif;
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.playlist-chip ion-icon {
  font-size: 1rem;
  color: var(--ion-color-orange);
}

.playlist-chip--success {
  background: rgba(45, 211, 111, 0.12);
  border-color: rgba(45, 211, 111, 0.35);
}

.playlist-chip--success ion-icon {
  color: var(--ion-color-success);
}

.playlist-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
  position: relative;
  z-index: 1;
}

.hero-action-btn {
  --border-color: rgba(255, 160, 60, 0.55);
  --color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  font-size: 0.8rem;
  --padding-start: 12px;
  --padding-end: 14px;
}

.hero-action-btn--danger {
  --border-color: rgba(235, 68, 90, 0.55);
}

@media (max-width: 520px) {
  .playlist-hero__inner {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .playlist-hero__chips {
    justify-content: center;
  }

  .playlist-hero__actions {
    justify-content: center;
  }
}

.playlist-tracks-scroll {
  max-height: calc(100vh - 300px) !important;
  min-height: 200px;
  --background: transparent;
}

@media (max-height: 700px) {
  .playlist-tracks-scroll {
    max-height: 38vh !important;
  }
}

h1 {
  letter-spacing: 0.08em;
}
.program-main {
  background-color: black;
  opacity: 0.9;
}
.program-item {
  --background: rgba(67, 67, 67, 0.7);
  color: white;
  --color-hover: white;
  font-weight: bold;
  --min-height: 100px;
  margin-bottom: 5px;
  border: 1px solid #434343;
}
.program-img-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
}
.program-img-wrapper img {
  scale: 0.7;
  object-fit: scale-down;
}
.release-item {
  --background: rgba(134, 134, 134, 0.5);
  color: rgb(226, 226, 226);
  --min-height: 80px;
  font-weight: bold;
  border: 1px solid #868686;
  border-top: unset;
}
.release-item ion-label {
  font-family: 'Montserrat';
}
.release-option {
  --background: var(--ion-color-orange);
}
.release-option ion-icon {
  color: white;
  scale: 1.5;
}
.release-container {
  position: relative;
}
.download-progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 100;
  background: var(--ion-color-orange);
  opacity: 0.6;
}
.add-playlist-button {
  font-size: 2em;
  --min-height: 60px;
  border: 1px solid #868686;
}
.playlist-container {
  --background: #1e1e1e !important;
}
.playlist-container ion-list {
  height: 100%;
  background: #1e1e1e !important;
}
.playlist-container ion-list-header {
  font-size: 1.5em;
  font-weight: bold;
  color: white;
}
.playlist-name {
  color: white !important;
  width: 80%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.playlist-name-input {
  font-family: 'Montserrat';
}
.playlist-header-label {
  color: white !important;
  width: 50%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.playlist-header-icon {
  font-size: 0.8em;
  color: white;
}
.playlist-header-button {
  color: white !important;
  --color: white !important;
  --background-activated: transparent !important;
  --border-color: var(--ion-color-orange);
}
.playlist-header-button ion-icon {
  font-size: 1.5em;
  margin-bottom: 3px;
  color: white !important;
}
.remove-download-button {
  font-size: 0.8em !important;
}
.column-buttons {
  display: flex;
  width: 100%;
  justify-content: center;
  flex-flow: column;
}
.download-button {
  display: flex;
  width: 100%;
  justify-content: center;
}
.download-button ion-button {
  --border-width: 0 !important;
}
/* Track Items */
.playlist-container ion-item {
  --background: rgba(255, 255, 255, 0.05);
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 64px;
  margin-bottom: 8px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.playlist-container ion-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    rgba(255, 140, 0, 0) 0%, 
    rgba(255, 140, 0, 0.05) 50%, 
    rgba(255, 140, 0, 0) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.playlist-container ion-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 140, 0, 0.3);
  transform: translateX(4px);
}

.playlist-container ion-item:hover::before {
  opacity: 1;
}

.playlist-container ion-item.playing {
  background: rgba(255, 140, 0, 0.15);
  border-color: rgba(255, 140, 0, 0.4);
}

.track-item {
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.track-number {
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  font-family: 'Montserrat', sans-serif;
}

.track-number span {
  transition: opacity 0.2s ease;
}

.playing-icon {
  opacity: 0;
  transition: all 0.3s ease;
  font-size: 1.2rem;
  color: var(--ion-color-orange);
}

.track-item.playing .track-number span {
  opacity: 0;
}

.track-item.playing .playing-icon {
  opacity: 1;
  color: var(--ion-color-orange);
}

.track-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-left: 16px;
  overflow: hidden;
}

.track-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Montserrat', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.track-item.playing .track-name {
  color: var(--ion-color-orange);
  font-weight: 600;
}

.track-duration {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
}

.track-item.playing .track-duration {
  color: rgba(255, 140, 0, 0.8);
}

.track-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.play-button-wrapper {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 140, 0, 0.15);
  border-radius: 50%;
  transition: all 0.3s ease;
  opacity: 0;
  transform: scale(0.8);
}

.track-item:hover .play-button-wrapper,
.track-item.playing .play-button-wrapper {
  opacity: 1;
  transform: scale(1);
  background: rgba(255, 140, 0, 0.25);
}

.play-icon {
  font-size: 1.2rem;
  color: var(--ion-color-orange);
}
.playlist-container ion-label {
  color: white;
}
.playlist-content .playlist-tracks-scroll {
  --background: transparent;
}
.playlist-content ion-label {
  color: white !important;
}
.playlist-content ion-item {
  color: white !important;
  --border-radius: 0 !important;
  margin-bottom: 2px !important;
}
.playlist-content ion-item-option {
  margin-bottom: 2px !important;
}
.add-button-container {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
}
.add-button-container ion-button {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  width: 50px;
  height: 50px;
  --border-radius: 25px;
}
.close-button {
  transform: rotateZ(45deg);
}
.add-button {
  color: #c0c0be;
}
.play-button {
  color: #c0c0be;
}
.preview-item {
  position: relative;
}
.preview-progress {
  position: absolute;
  bottom: 0;
  height: 2px;
  background: var(--ion-color-orange);
}
.danger-button {
  color: var(--ion-color-danger);
}
.danger-background ion-button {
  --background: var(--ion-color-danger) !important;
}
.playlist-fake-item {
  --background: transparent !important;
}
ion-modal {
  --background: #1e1e1e;
}
ion-toolbar {
  --background: #1e1e1e;
}
ion-text {
  color: white;
}
.download-progress-bar {
  height: 5px;
  --progress-background: var(--ion-color-orange);
  --background: #c0c0be;
}
.fake-footer {
  height: 20vh;
}
</style>